import { useState, useEffect } from 'react';
import { useUser } from '../components/UserContext';
import { takePrefetchedQuestions } from '../lib/questionsBoot';

/**
 * Pobieranie zestawu pytań do symulatora egzaminu.
 *
 * Zapytanie startuje jeszcze przed wczytaniem bundle'a - z inline'owego skryptu
 * w public/index.html, który parkuje obietnicę na window.__QUESTIONS_BOOT
 * (patrz src/lib/questionsBoot.js). Ten hook tylko ją odbiera, a jeżeli jej nie
 * ma albo dotyczy innego trybu, pyta API normalną ścieżką.
 *
 * Tryb demo vs pełny rozstrzyga `user.test_access_till`. Przy obecnym tokenie
 * czekamy na rozstrzygnięcie stanu użytkownika, żeby nie pobrać najpierw demo,
 * a chwilę później pełnego zestawu.
 */
export const useExam = ({ patent, grupa = null }) => {
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  const isTestAccessValid = user && user.test_access_till
    ? new Date() < new Date(user.test_access_till)
    : false;

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('access_token');
  const userResolved = !hasToken || user !== null;

  useEffect(() => {
    if (!userResolved || !patent) return undefined;

    let cancelled = false;
    const demo = !isTestAccessValid;

    const pobierz = async () => {
      setLoading(true);
      try {
        // Prefetch z index.html dotyczy tylko pełnego egzaminu, nie trybu nauki.
        if (!grupa) {
          const prefetched = takePrefetchedQuestions(demo);
          if (prefetched) {
            try {
              const dane = await prefetched;
              if (!cancelled && Array.isArray(dane) && dane.length) {
                setQuestions(dane);
                setLoading(false);
                return;
              }
            } catch (e) {
              /* spadamy do zwykłego zapytania poniżej */
            }
          }
        }

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const headers = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('access_token');
        if (token) headers.Authorization = `Bearer ${token}`;

        const endpoint = grupa ? 'get_questions_by_group' : 'get_exam';
        const body = grupa ? { patent, grupa, demo } : { patent, demo };

        const odp = await fetch(`${API_BASE_URL}/zeglarka/${endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });

        if (!odp.ok) throw new Error('Nie udało się pobrać pytań');

        const dane = await odp.json();
        if (!cancelled) setQuestions(dane);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    pobierz();

    return () => {
      cancelled = true;
    };
  }, [patent, grupa, isTestAccessValid, userResolved]);

  return { questions, error, loading, demo: !isTestAccessValid };
};
